import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { getCryptoPrices } from '../services/api';

const COINS = [
  { id: 'BTC', name: 'Bitcoin', color: '#F7931A' },
  { id: 'ETH', name: 'Ethereum', color: '#627EEA' },
  { id: 'USDT', name: 'Tether', color: '#26A17B' },
  { id: 'BNB', name: 'BNB', color: '#F3BA2F' },
  { id: 'SOL', name: 'Solana', color: '#9945FF' },
  { id: 'XRP', name: 'XRP', color: '#00AAE4' },
  { id: 'ADA', name: 'Cardano', color: '#0033AD' },
  { id: 'DOGE', name: 'Dogecoin', color: '#C2A633' },
  { id: 'TRX', name: 'TRON', color: '#EF0027' },
  { id: 'TON', name: 'Toncoin', color: '#0098EA' },
  { id: 'AVAX', name: 'Avalanche', color: '#E84142' },
  { id: 'DOT', name: 'Polkadot', color: '#E6007A' },
  { id: 'LINK', name: 'Chainlink', color: '#2A5ADA' },
  { id: 'LTC', name: 'Litecoin', color: '#BFBBBB' },
  { id: 'BCH', name: 'Bitcoin Cash', color: '#8DC351' },
  { id: 'XLM', name: 'Stellar', color: '#14B6E7' },
  { id: 'SHIB', name: 'Shiba Inu', color: '#FFA409' },
  { id: 'ATOM', name: 'Cosmos', color: '#2E3148' },
  { id: 'POL', name: 'Polygon', color: '#8247E5' },
  { id: 'UNI', name: 'Uniswap', color: '#FF007A' },
];

const formatPrice = (price) => {
  if (!price || price === 0) return '$0.00';
  if (price >= 1) return '$' + price.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (price >= 0.01) return '$' + price.toFixed(4);
  return '$' + price.toFixed(6);
};

const CoinChip = ({ coin, price }) => {
  const isUp = price.change_24h >= 0;
  return (
    <div className="ticker-chip">
      <div
        className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
        style={{ background: `${coin.color}30`, border: `1px solid ${coin.color}55` }}
      >
        {coin.id.charAt(0)}
      </div>
      <span className="text-white text-xs font-semibold whitespace-nowrap">{coin.id}</span>
      <span className="text-slate-400 text-xs font-medium whitespace-nowrap">{formatPrice(price.price)}</span>
      <span className={`flex items-center gap-px text-[11px] font-medium whitespace-nowrap ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
        {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {Math.abs(price.change_24h || 0).toFixed(2)}%
      </span>
    </div>
  );
};

const CryptoTicker = ({ refreshKey }) => {
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, [refreshKey]);

  const fetchPrices = async () => {
    try {
      const res = await getCryptoPrices();
      setPrices(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex gap-3 overflow-hidden py-1" data-testid="crypto-ticker-loading">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="flex-shrink-0 w-36 h-10 rounded-lg bg-white/[0.03] animate-pulse" />
        ))}
      </div>
    );
  }

  const available = COINS.filter(c => prices[c.id]?.price);
  if (!available.length) return null;

  return (
    <div
      className="relative overflow-hidden"
      data-testid="crypto-ticker"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-[#060a13] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-[#060a13] to-transparent z-10 pointer-events-none" />

      <div
        ref={trackRef}
        className="ticker-track"
        style={{ animationPlayState: paused ? 'paused' : 'running' }}
      >
        {/* Render coins twice for seamless loop */}
        {[0, 1].map((set) => (
          <div key={set} className="flex gap-3 pr-3" aria-hidden={set === 1}>
            {available.map((coin) => (
              <CoinChip key={`${set}-${coin.id}`} coin={coin} price={prices[coin.id]} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CryptoTicker;
